import { httpClient } from '@bs/core'
import {
  LOYALTY_ADJUST_REASON_LABEL,
  PROMOTION_STATUS_VALUE,
} from '../../types/loyalty'
import type {
  AppleWalletPassConfig,
  AppleWalletPassConfigInput,
  CreatePromotionInput,
  UpdatePromotionInput,
  LoyaltyActivityEvent,
  LoyaltyActivityEventsParams,
  LoyaltyActivityEventsResponse,
  LoyaltyActivityKind,
  LoyaltyActivitySummary,
  LoyaltyActivityType,
  LoyaltyAdjustPointsRequest,
  LoyaltyAdjustPointsResponse,
  LoyaltyMember,
  LoyaltyMemberLogEntry,
  LoyaltyMemberLogSummary,
  LoyaltyMemberLogsParams,
  LoyaltyMemberLogsResponse,
  LoyaltyMemberStatus,
  LoyaltyMembersParams,
  LoyaltyMembersResponse,
  LoyaltyOverview,
  LoyaltyOverviewSummary,
  LoyaltyPointsActivity,
  LoyaltyPopularCoupon,
  LoyaltyRecentActivity,
  LoyaltyRules,
  LoyaltyTier,
  LoyaltyTierDistribution,
  LoyaltyTopEarner,
  Promotion,
  PromotionListResponse,
  PromotionStatusValue,
  PromotionsMembershipPricing,
  PromotionsSettingsDocument,
} from '../../types/loyalty'

// httpClient.json.* unwraps res.data.data → the resolved value is the inner
// `data` payload directly, not the full SuccessResponse envelope.
// See spec: breadstack_backend/docs/task_specs/STORE_LOYALTY_API.md

// ── URL builders ──────────────────────────────────────────────────────────────

const rulesUrl = (storeId: string) => `/stores/${storeId}/loyalty/rules`
const membersUrl = (storeId: string) => `/stores/${storeId}/loyalty/members`
const overviewUrl = (storeId: string) => `/stores/${storeId}/loyalty/overview`
const memberLogsUrl = (storeId: string, customerId: number) =>
  `/stores/${storeId}/loyalty/members/${customerId}/logs`
// Activity tab list endpoint (confirmed 2026-05-21). Backend returns the
// store-scoped activity log with paging + a summary block; see the
// `LoyaltyActivityEventsResponse` type doc for the full payload shape.
const activityEventsUrl = (storeId: string) =>
  `/stores/${storeId}/loyalty/activities`
// Admin points adjustment endpoint (PUT). Confirmed wire contract:
//   PUT /stores/:storeId/loyalty/members/:customerId/adjust-points
//   { action: "add" | "subtract", amount: number, reason: string, note: string }
// Note that `reason` is sent as the human-readable label (e.g. "Correction")
// — matches what surfaces in the activity log.
const memberAdjustPointsUrl = (storeId: string, customerId: number) =>
  `/stores/${storeId}/loyalty/members/${customerId}/adjust-points`
// Promotion list endpoint — drives the Membership Pricing table. One row per
// promotion record. Source: frontend/src/pages/Promotion/hooks/usePromotionList.js
const promotionsListUrl = (storeId: string) => `/stores/${storeId}/promotions`
// Legacy promotion-settings endpoint — re-used here as the persistence store for
// per-location membership pricing. The global `promotions_settings` field is
// round-tripped untouched so the existing Promotion-module flows are unaffected.
// Source: frontend/src/pages/Promotion/hooks/usePromotionSetting.js
const promotionsSettingsUrl = (storeId: string) =>
  `/stores/${storeId}/promotions_settings`
// Apple Wallet Pass config endpoint — one document per store (upsert on POST).
// Spec: breadstack_backend/docs/task_specs/APPLE_WALLET_PASS_CONFIG_API.md.
const appleWalletPassConfigUrl = (storeId: string) =>
  `/stores/${storeId}/apple-wallet-pass-config`

// ── Raw wire shapes (snake_case mirror of the backend payloads) ───────────────

interface ApiLoyaltyMember {
  // Confirmed 2026-05-21: the members list endpoint emits the member's
  // customer-scoped id under `id` (not `customer_id`). `customer_id` is kept
  // as a legacy fallback in case older deployments still emit it. Both forms
  // may arrive as a number or numeric string; `mapMember` normalises to a
  // finite number and feeds it as `customerId` to the rest of the app — and
  // ultimately as the `:customerId` path segment on the Adjust Points PUT.
  id?: number | string
  customer_id?: number | string
  // POS-system customer id, used by the host app's Customer detail page
  // (`/s/:storeId/customers/:posCustomerId/view`). Distinct from the loyalty
  // `id`/`customer_id`. Backend ships it as a string; tolerate number for
  // safety. May be missing on pre-POS-linked accounts.
  pos_customer_id?: number | string
  first_name?: string
  last_name?: string
  email?: string
  loyalty_tier?: LoyaltyTier
  points_balance?: number
  lifetime_earned_points?: number
  lifetime_redeem_points?: number
  loyalty_last_activity?: string | null
  status?: LoyaltyMemberStatus
}

interface ApiLoyaltyMembersResponse {
  items?: ApiLoyaltyMember[]
  total_items?: number
  total_pages?: number
  page?: number
  page_size?: number
}

interface ApiLoyaltyOverviewSummary {
  total_members?: number
  new_members_this_month?: number
  points_in_circulation?: number
  points_in_value?: number
  total_earned?: number
  total_earned_change_pct?: number
  total_redeemed?: number
  redemption_rate_pct?: number
}

interface ApiLoyaltyTierDistribution {
  tier?: LoyaltyTier | 'unknown'
  count?: number
  percentage?: number
}

interface ApiLoyaltyTopEarner {
  first_name?: string
  last_name?: string
  email?: string
  lifetime_earned_points?: number
}

interface ApiLoyaltyPointsActivity {
  year?: number
  month?: number
  earned?: number
  redeemed?: number
}

interface ApiLoyaltyRecentActivity {
  title?: string
  points?: number
  type?: LoyaltyRecentActivity['type']
  activity?: LoyaltyRecentActivity['activity']
  datetime?: string
  order_id?: number
  customer_name?: string
}

// Wire shape for the Popular Coupons widget on the Overview tab. Confirmed
// 2026-05-21: backend ships the list under `top_coupons` (not the earlier
// placeholder `popular_coupons`). Items carry id/code/description/used/
// revenue/discount; the `discount_type` field is no longer present so the UI
// treats the value as fixed-dollar by default.
interface ApiLoyaltyTopCoupon {
  id?: number
  code?: string
  description?: string
  icon?: string
  icon_bg?: string
  discount?: number
  used?: number
  revenue?: number
}

interface ApiLoyaltyOverview {
  summary?: ApiLoyaltyOverviewSummary
  tier_distribution?: ApiLoyaltyTierDistribution[]
  top_earners?: ApiLoyaltyTopEarner[]
  points_activity?: ApiLoyaltyPointsActivity[]
  recent_activity?: ApiLoyaltyRecentActivity[]
  // Confirmed field name (2026-05-21). The legacy `popular_coupons` alias is
  // kept here as a defensive read so older deployments don't break the widget.
  top_coupons?: ApiLoyaltyTopCoupon[]
  popular_coupons?: ApiLoyaltyTopCoupon[]
}

interface ApiLoyaltyMemberLogSummary {
  loyalty_tier?: LoyaltyTier
  points_balance?: number
  lifetime_earned_points?: number
  lifetime_redeem_points?: number
}

interface ApiLoyaltyMemberLogEntry {
  customer_id?: number
  title?: string
  points?: number
  type?: LoyaltyMemberLogEntry['type']
  activity?: LoyaltyMemberLogEntry['activity']
  datetime?: string
  order_id?: number
}

interface ApiLoyaltyMemberLogsResponse {
  summary?: ApiLoyaltyMemberLogSummary
  items?: ApiLoyaltyMemberLogEntry[]
  total_items?: number
  total_pages?: number
  page?: number
  page_size?: number
}

// ── Mappers (snake_case → camelCase) ──────────────────────────────────────────

// Defensive numeric coercion — backend sometimes ships floats for fields that
// the UI displays as currency. NaN / Infinity / undefined collapse to 0.
const toFiniteNumber = (raw: number | undefined): number =>
  typeof raw === 'number' && Number.isFinite(raw) ? raw : 0

// Coerce a customer_id that may arrive as a number, a numeric string, or
// be missing entirely. Returns a finite number, or undefined when no usable
// value is present. Anchored regex prevents partial matches like "10abc".
const coerceCustomerId = (raw: number | string | undefined): number | undefined => {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw
  if (typeof raw === 'string' && /^\d+$/.test(raw)) return Number.parseInt(raw, 10)
  return undefined
}

const mapMember = (raw: ApiLoyaltyMember): LoyaltyMember => {
  // Prefer the confirmed `id` field; fall back to legacy `customer_id` for
  // deployments that still emit the older name. This id is what the Adjust
  // Points PUT consumes as its `:customerId` path segment.
  const customerId = coerceCustomerId(raw.id) ?? coerceCustomerId(raw.customer_id)
  // POS-system customer id — drives the host app's Customer detail URL.
  // Coerced to a string for use in URL building; left undefined when missing.
  const posCustomerIdRaw = raw.pos_customer_id
  const posCustomerId =
    typeof posCustomerIdRaw === 'string' && posCustomerIdRaw.trim().length > 0
      ? posCustomerIdRaw.trim()
      : typeof posCustomerIdRaw === 'number' && Number.isFinite(posCustomerIdRaw)
        ? String(posCustomerIdRaw)
        : undefined
  return {
  // Pass through only when present; downstream code uses customerId to enable
  // the per-member logs query, so leaving it undefined disables that fetch.
  ...(typeof customerId === 'number' ? { customerId } : {}),
  ...(posCustomerId ? { posCustomerId } : {}),
  firstName: raw.first_name ?? '',
  lastName: raw.last_name ?? '',
  email: raw.email ?? '',
  loyaltyTier: raw.loyalty_tier ?? null,
  pointsBalance: raw.points_balance ?? 0,
  lifetimeEarnedPoints: raw.lifetime_earned_points ?? 0,
  lifetimeRedeemPoints: raw.lifetime_redeem_points ?? 0,
  loyaltyLastActivity: raw.loyalty_last_activity ?? null,
  status: raw.status ?? null,
  }
}

const mapOverviewSummary = (raw: ApiLoyaltyOverviewSummary | undefined): LoyaltyOverviewSummary => ({
  totalMembers: raw?.total_members ?? 0,
  newMembersThisMonth: raw?.new_members_this_month ?? 0,
  pointsInCirculation: raw?.points_in_circulation ?? 0,
  pointsInValue: raw?.points_in_value ?? 0,
  totalEarned: raw?.total_earned ?? 0,
  totalEarnedChangePct: raw?.total_earned_change_pct ?? 0,
  totalRedeemed: raw?.total_redeemed ?? 0,
  redemptionRatePct: raw?.redemption_rate_pct ?? 0,
})

const mapTierDistribution = (raw: ApiLoyaltyTierDistribution): LoyaltyTierDistribution => ({
  tier: raw.tier ?? 'unknown',
  count: raw.count ?? 0,
  percentage: raw.percentage ?? 0,
})

const mapTopEarner = (raw: ApiLoyaltyTopEarner): LoyaltyTopEarner => ({
  firstName: raw.first_name ?? '',
  lastName: raw.last_name ?? '',
  email: raw.email ?? '',
  lifetimeEarnedPoints: raw.lifetime_earned_points ?? 0,
})

const mapPointsActivity = (raw: ApiLoyaltyPointsActivity): LoyaltyPointsActivity => ({
  year: raw.year ?? 0,
  month: raw.month ?? 0,
  earned: raw.earned ?? 0,
  redeemed: raw.redeemed ?? 0,
})

const mapRecentActivity = (raw: ApiLoyaltyRecentActivity): LoyaltyRecentActivity => ({
  title: raw.title ?? '',
  points: raw.points ?? 0,
  type: raw.type ?? 'earned',
  activity: raw.activity ?? 'order_complete',
  datetime: raw.datetime ?? '',
  orderId: raw.order_id,
  // Spec: customer_name may be a single space when the lookup fails — trim defensively.
  customerName: (raw.customer_name ?? '').trim(),
})

// Confirmed mapper (2026-05-21). Field names mirror the `top_coupons` wire
// shape one-to-one — see the `LoyaltyPopularCoupon` type doc for the contract.
const mapPopularCoupon = (raw: ApiLoyaltyTopCoupon): LoyaltyPopularCoupon => ({
  ...(typeof raw.id === 'number' && Number.isFinite(raw.id) ? { id: raw.id } : {}),
  code: raw.code ?? '',
  description: raw.description ?? '',
  icon: raw.icon,
  iconBg: raw.icon_bg,
  discount: toFiniteNumber(raw.discount),
  used: typeof raw.used === 'number' && Number.isFinite(raw.used) ? raw.used : 0,
  revenue: toFiniteNumber(raw.revenue),
})

const mapMemberLogSummary = (
  raw: ApiLoyaltyMemberLogSummary | undefined,
): LoyaltyMemberLogSummary => ({
  loyaltyTier: raw?.loyalty_tier ?? 'starter',
  pointsBalance: raw?.points_balance ?? 0,
  lifetimeEarnedPoints: raw?.lifetime_earned_points ?? 0,
  lifetimeRedeemPoints: raw?.lifetime_redeem_points ?? 0,
})

const mapMemberLogEntry = (raw: ApiLoyaltyMemberLogEntry): LoyaltyMemberLogEntry => ({
  customerId: raw.customer_id ?? 0,
  title: raw.title ?? '',
  points: raw.points ?? 0,
  type: raw.type ?? 'earned',
  activity: raw.activity ?? 'order_complete',
  datetime: raw.datetime ?? '',
  orderId: raw.order_id,
})

// ── Public API ────────────────────────────────────────────────────────────────

export const getLoyaltyRules = async (storeId: string): Promise<LoyaltyRules> => {
  // Pass through unchanged — the rules document is snake_case end-to-end so the
  // PUT body can be the same shape we read.
  return (await httpClient.json.get(rulesUrl(storeId))) as LoyaltyRules
}

export const updateLoyaltyRules = async (
  storeId: string,
  body: LoyaltyRules,
): Promise<LoyaltyRules> => {
  return (await httpClient.json.put(rulesUrl(storeId), body)) as LoyaltyRules
}

// Store-level loyalty program on/off. Reuses the same endpoint the Settings →
// Integrations → Breadstack Loyalty screen uses (PUT /stores/bs-loyalty with
// { store_id, connection }); `connection: true` enables the program, `false`
// disables it. This flips the store's `enable_bs_loyalty` flag — no dedicated
// pantry rules field is involved.
export const setBsLoyaltyEnabled = async (
  storeId: string,
  connection: boolean,
): Promise<void> => {
  await httpClient.json.put('/stores/bs-loyalty', {
    store_id: storeId,
    connection,
  })
}

export const getLoyaltyMembers = async (
  storeId: string,
  params: LoyaltyMembersParams,
): Promise<LoyaltyMembersResponse> => {
  const raw = (await httpClient.json.get(membersUrl(storeId), {
    params: {
      page: params.page,
      page_size: params.page_size,
      ...(params.search ? { search: params.search } : {}),
      ...(params.loyalty_tier ? { loyalty_tier: params.loyalty_tier } : {}),
      ...(params.status ? { status: params.status } : {}),
      ...(params.sort_by ? { sort_by: params.sort_by } : {}),
      ...(typeof params.sort === 'number' ? { sort: params.sort } : {}),
    },
  })) as ApiLoyaltyMembersResponse

  return {
    items: (raw?.items ?? []).map(mapMember),
    page: raw?.page ?? params.page,
    pageSize: raw?.page_size ?? params.page_size,
    totalItems: raw?.total_items ?? 0,
    totalPages: raw?.total_pages ?? 0,
  }
}

export const getLoyaltyOverview = async (storeId: string): Promise<LoyaltyOverview> => {
  const raw = (await httpClient.json.get(overviewUrl(storeId))) as ApiLoyaltyOverview
  return {
    summary: mapOverviewSummary(raw?.summary),
    tierDistribution: (raw?.tier_distribution ?? []).map(mapTierDistribution),
    topEarners: (raw?.top_earners ?? []).map(mapTopEarner),
    pointsActivity: (raw?.points_activity ?? []).map(mapPointsActivity),
    recentActivity: (raw?.recent_activity ?? []).map(mapRecentActivity),
    // Confirmed wire field is `top_coupons` (2026-05-21). `popular_coupons`
    // is retained as a defensive fallback for any deployment that still emits
    // the legacy name; new builds populate the former.
    popularCoupons: (raw?.top_coupons ?? raw?.popular_coupons ?? []).map(
      mapPopularCoupon,
    ),
  }
}

// ── Points adjustment ────────────────────────────────────────────────────────
// Wire contract (confirmed by user 2026-05-21):
//   PUT /stores/:storeId/loyalty/members/:customerId/adjust-points
//   body { action, amount, reason, note }
// Internal API keeps `direction` + reason-slug for ergonomic UI code; the
// gateway translates to the wire names on the way out. Response shape isn't
// part of the confirmed spec yet — every field is treated as optional and the
// mutation hook re-fetches members / logs / overview on success regardless.

interface ApiAdjustPointsResponse {
  customer_id?: number
  points_balance?: number
  lifetime_earned_points?: number
  lifetime_redeem_points?: number
}

export const adjustLoyaltyPoints = async (
  storeId: string,
  customerId: number,
  body: LoyaltyAdjustPointsRequest,
): Promise<LoyaltyAdjustPointsResponse> => {
  const raw = (await httpClient.json.put(
    memberAdjustPointsUrl(storeId, customerId),
    {
      action: body.direction,
      amount: body.amount,
      reason: LOYALTY_ADJUST_REASON_LABEL[body.reason],
      note: body.note ?? '',
    },
  )) as ApiAdjustPointsResponse | null
  return {
    customerId: raw?.customer_id ?? customerId,
    ...(typeof raw?.points_balance === 'number'
      ? { pointsBalance: raw.points_balance }
      : {}),
    ...(typeof raw?.lifetime_earned_points === 'number'
      ? { lifetimeEarnedPoints: raw.lifetime_earned_points }
      : {}),
    ...(typeof raw?.lifetime_redeem_points === 'number'
      ? { lifetimeRedeemPoints: raw.lifetime_redeem_points }
      : {}),
  }
}

export const getLoyaltyMemberLogs = async (
  storeId: string,
  customerId: number,
  params: LoyaltyMemberLogsParams,
): Promise<LoyaltyMemberLogsResponse> => {
  const raw = (await httpClient.json.get(memberLogsUrl(storeId, customerId), {
    params: {
      page: params.page,
      page_size: params.page_size,
    },
  })) as ApiLoyaltyMemberLogsResponse

  return {
    summary: mapMemberLogSummary(raw?.summary),
    items: (raw?.items ?? []).map(mapMemberLogEntry),
    page: raw?.page ?? params.page,
    pageSize: raw?.page_size ?? params.page_size,
    totalItems: raw?.total_items ?? 0,
    totalPages: raw?.total_pages ?? 0,
  }
}

// ── Activity events (Activity tab) ───────────────────────────────────────────
// Wire shape mirrors the confirmed backend payload (2026-05-21). Each item is
// roughly a `LoyaltyMemberLogEntry` with extra fields surfacing the owning
// member + admin attribution — see the `LoyaltyActivityEvent` type doc.

interface ApiLoyaltyActivityEvent {
  store_id?: string
  customer_id?: number | string
  admin_id?: string
  admin_name?: string
  datetime?: string
  // Widened to `string`: the wire also emits credit verbs beyond the
  // earned/redeemed axis (e.g. `refund_redeem` when a redeemed coupon is
  // removed). `normalizeActivityType` maps those back onto the domain union.
  type?: string
  activity?: LoyaltyActivityKind
  title?: string
  points?: number
  points_balance?: number
  member_name?: string
}

// Collapse the wire `type` onto the domain earned/redeemed (credit/debit) axis.
// Only an explicit `redeemed` is a debit; everything else — `earned`, the
// `refund_redeem` returned-points credit, and unknown/missing verbs — presents
// as a credit so refunds don't render as a purple "redeemed" row. The specific
// verb is preserved on `activity` to drive the icon.
const normalizeActivityType = (raw?: string): LoyaltyActivityType =>
  raw === 'redeemed' ? 'redeemed' : 'earned'

interface ApiLoyaltyActivitySummary {
  total_events?: number
  total_value_redeemed?: number
  point_activity_count?: number
  point_activity_value?: number
  coupons_redeemed_count?: number
  coupons_redeemed_value?: number
}

interface ApiLoyaltyActivityEventsResponse {
  summary?: ApiLoyaltyActivitySummary
  items?: ApiLoyaltyActivityEvent[]
  total_items?: number
  total_pages?: number
  page?: number
  page_size?: number
}

const mapActivitySummary = (
  raw: ApiLoyaltyActivitySummary | undefined,
): LoyaltyActivitySummary => ({
  totalEvents: raw?.total_events ?? 0,
  totalValueRedeemed: toFiniteNumber(raw?.total_value_redeemed),
  pointActivityCount: raw?.point_activity_count ?? 0,
  pointActivityValue: toFiniteNumber(raw?.point_activity_value),
  couponsRedeemedCount: raw?.coupons_redeemed_count ?? 0,
  couponsRedeemedValue: toFiniteNumber(raw?.coupons_redeemed_value),
})

const mapActivityEvent = (
  raw: ApiLoyaltyActivityEvent,
  index: number,
): LoyaltyActivityEvent => {
  const customerId = coerceCustomerId(raw.customer_id)
  // The wire has no top-level id; synthesise a stable React key from the
  // tuple (datetime, customer_id, activity, index). Index is appended last so
  // rapid back-to-back rows (manual adjust pairs at the same second) still
  // collide-free.
  const id = `${raw.datetime ?? ''}-${customerId ?? 'na'}-${raw.activity ?? 'na'}-${index}`
  return {
    id,
    ...(raw.store_id ? { storeId: raw.store_id } : {}),
    ...(typeof customerId === 'number' ? { customerId } : {}),
    memberName: raw.member_name ?? '',
    ...(raw.admin_id ? { adminId: raw.admin_id } : {}),
    ...(raw.admin_name ? { adminName: raw.admin_name } : {}),
    type: normalizeActivityType(raw.type),
    activity: raw.activity ?? 'order_complete',
    title: raw.title ?? '',
    points: typeof raw.points === 'number' ? raw.points : 0,
    ...(typeof raw.points_balance === 'number'
      ? { pointsBalance: raw.points_balance }
      : {}),
    datetime: raw.datetime ?? '',
  }
}

export const getLoyaltyActivityEvents = async (
  storeId: string,
  params: LoyaltyActivityEventsParams,
): Promise<LoyaltyActivityEventsResponse> => {
  const raw = (await httpClient.json.get(activityEventsUrl(storeId), {
    params: {
      // Backend param names: tab + date_range (not filter + range).
      tab: params.filter ?? 'all',
      date_range: params.range,
      ...(params.search ? { search: params.search } : {}),
      ...(typeof params.page === 'number' ? { page: params.page } : {}),
      ...(typeof params.page_size === 'number'
        ? { page_size: params.page_size }
        : {}),
    },
  })) as ApiLoyaltyActivityEventsResponse | null

  return {
    summary: mapActivitySummary(raw?.summary),
    items: (raw?.items ?? []).map(mapActivityEvent),
    page: raw?.page ?? params.page ?? 1,
    pageSize: raw?.page_size ?? params.page_size ?? 25,
    totalItems: raw?.total_items ?? 0,
    totalPages: raw?.total_pages ?? 0,
  }
}

// ── Promotion list (Membership Pricing data source) ──────────────────────────

interface ApiPromotionAllProducts {
  discount_amount?: number
  discount_type?: string
}

interface ApiPromotionRecord {
  _id?: string
  name?: string
  status?: number
  location_ids?: Array<string | number>
  all_products?: ApiPromotionAllProducts | null
  start_date?: string
  end_date?: string
}

interface ApiPromotionListResponse {
  data?: ApiPromotionRecord[]
  page?: number
  limit?: number
  total_items?: number
  total_pages?: number
}

const mapPromotion = (raw: ApiPromotionRecord): Promotion => ({
  id: String(raw._id ?? ''),
  name: raw.name ?? '',
  // Backend uses numeric status codes (-1..3); see
  // frontend/src/constants/promotion.ts STATUS_VALUES.
  status: ((typeof raw.status === 'number' ? raw.status : -1) as PromotionStatusValue),
  locationIds: (raw.location_ids ?? []).map((id) => String(id)),
  allProducts: raw.all_products
    ? {
        discountAmount: Number.isFinite(raw.all_products.discount_amount)
          ? Number(raw.all_products.discount_amount)
          : 0,
        discountType: raw.all_products.discount_type,
      }
    : null,
  startDate: raw.start_date,
  endDate: raw.end_date,
  // Untouched original record. Cast through `unknown` because the wire shape
  // carries many fields not enumerated in `ApiPromotionRecord` — the update
  // PUT needs to echo every one of them back to keep the server-side record
  // intact.
  raw: raw as unknown as Record<string, unknown>,
})

export interface PromotionListParams {
  page?: number
  limit?: number
  // Optional overrides for the membership-pricing defaults below. Pass an
  // empty string to omit a filter entirely on a one-off call.
  customerTypes?: string
  productTypes?: string
  promotionTypes?: string
}

// Yesterday's local date as YYYY-MM-DD. Backdating `start_date` by one day
// guarantees the freshly-minted promotion is already past its start instant —
// the backend's WAITING→ACTIVE flip happens on the start boundary, so a
// same-day or forward-dated value risks leaving the row stuck in WAITING
// until the clock catches up. `start_time` is intentionally NOT shifted; it
// stays at the membership-pricing default of `00:00` so the promotion runs
// the full local day.
const yesterdayIso = (): string => {
  const d = new Date(Date.now() - 24 * 60 * 60 * 1000)
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

/**
 * Mint a new promotion record for the Membership Pricing → Add Location flow.
 *
 * Defaults bake in the membership-pricing contract: basic promotion type,
 * `all` product type with a percentage discount, `registered` customer type,
 * no end date, `repeating_indefinite = false`, single-location targeting via
 * `location_ids: [locationId]`, and status WAITING (0) so the new row is
 * scheduled to run — the backend flips WAITING → ACTIVE on the start
 * boundary, and the row's toggle reads as ON immediately (the toggle
 * treats WAITING and ACTIVE as "on").
 *
 * Body shape matches the legacy `revertNewDraftToApi` mapper for the fields
 * the backend requires on POST `/stores/:storeId/promotions`.
 */
export const createPromotion = async (
  storeId: string,
  input: CreatePromotionInput,
): Promise<Promotion> => {
  const pct = Math.max(
    0,
    Math.min(
      100,
      Math.round(Number.isFinite(input.discountPercent) ? input.discountPercent : 0),
    ),
  )
  const body = {
    name: input.name,
    status: PROMOTION_STATUS_VALUE.WAITING,
    color: '#2766B1',
    start_date: yesterdayIso(),
    end_date: null,
    start_time: '00:00',
    end_time: '23:59',
    // Hard-fix: always sent as every day of the week (Sun..Sat = 0..6)
    // so membership pricing applies year-round, regardless of the day.
    frequency: [0, 1, 2, 3, 4, 5, 6] as number[],
    repeating_indefinite: false,
    promotion_type: 'basic',
    product_type: 'all',
    customer_type: 'registered',
    all_products: {
      discount_amount: pct / 100,
      discount_type: 'percentage',
    },
    brands: {},
    categories: {},
    products: {},
    location_ids: [input.locationId],
  }
  const raw = (await httpClient.json.post(
    promotionsListUrl(storeId),
    body,
  )) as ApiPromotionRecord | null
  return raw ? mapPromotion(raw) : mapPromotion({ _id: '', name: body.name })
}

/**
 * Update a single promotion record. Used by the Membership Pricing Save
 * panel to walk dirty rows one-by-one (no bulk-edit endpoint exists yet).
 *
 * The PUT endpoint expects a full record, so the caller passes the current
 * raw promotion under `input.current`. The gateway spreads that as the
 * base body and overrides only the fields the user actually changed —
 * status and/or all_products — so every other field (color, frequency,
 * customer_type, brands, categories, products, etc.) round-trips intact.
 *
 * `_id` is dropped from the body because the id is already in the URL.
 *
 * Endpoint: PUT `/stores/:storeId/promotions/:promotionId` (mirrors the
 * legacy `promotionUpdateApi` in `Promotion/hooks/usePromotionDetail.tsx`).
 */
export const updatePromotion = async (
  storeId: string,
  promotionId: string,
  input: UpdatePromotionInput,
): Promise<Promotion> => {
  // Start from the current record so untouched fields are preserved on the
  // round-trip. Shallow-spread is safe here because the overrides below
  // replace whole top-level keys (`status`, `all_products`).
  const body: Record<string, unknown> = { ...(input.current ?? {}) }
  // The id lives in the URL, not the body.
  delete body._id

  if (typeof input.status === 'number') {
    body.status = input.status
  }
  if (typeof input.discountPercent === 'number') {
    const pct = Math.max(
      0,
      Math.min(100, Math.round(input.discountPercent)),
    )
    body.all_products = {
      discount_amount: pct / 100,
      discount_type: 'percentage',
    }
  }
  const raw = (await httpClient.json.put(
    `${promotionsListUrl(storeId)}/${promotionId}`,
    body,
  )) as ApiPromotionRecord | null
  return raw
    ? mapPromotion(raw)
    : mapPromotion({ _id: promotionId })
}

export const getPromotions = async (
  storeId: string,
  params: PromotionListParams = {},
): Promise<PromotionListResponse> => {
  // Membership Pricing defaults — filter the promotion list to basic
  // all-products promotions targeting registered members. Each filter is sent
  // as a single string value under the backend's plural snake_case name.
  const customerTypes = params.customerTypes ?? 'registered'
  const productTypes = params.productTypes ?? 'all'
  const promotionTypes = params.promotionTypes ?? 'basic'

  const raw = (await httpClient.json.get(promotionsListUrl(storeId), {
    params: {
      page: params.page ?? 1,
      limit: params.limit ?? 25,
      ...(customerTypes ? { customer_types: customerTypes } : {}),
      ...(productTypes ? { product_types: productTypes } : {}),
      ...(promotionTypes ? { promotion_types: promotionTypes } : {}),
    },
  })) as ApiPromotionListResponse | null
  return {
    items: (raw?.data ?? []).map(mapPromotion),
    page: raw?.page ?? params.page ?? 1,
    pageSize: raw?.limit ?? params.limit ?? 25,
    totalItems: raw?.total_items ?? 0,
    totalPages: raw?.total_pages ?? 0,
  }
}

// ── Promotion settings (legacy — kept for the round-trip save path) ──────────

interface ApiPromotionsMembershipPricingLocation {
  warehouse_id?: string
  id?: string | number
  enabled?: boolean
  discount_percent?: number
}

interface ApiPromotionsMembershipPricing {
  locations?: ApiPromotionsMembershipPricingLocation[]
}

interface ApiPromotionsSettingsDocument {
  promotions_settings?: string
  membership_pricing?: ApiPromotionsMembershipPricing
}

const mapMembershipPricing = (
  raw: ApiPromotionsMembershipPricing | undefined,
): PromotionsMembershipPricing => ({
  locations: (raw?.locations ?? []).map((loc) => ({
    warehouse_id: String(loc.warehouse_id ?? loc.id ?? ''),
    enabled: Boolean(loc.enabled),
    discount_percent: Number.isFinite(loc.discount_percent)
      ? Number(loc.discount_percent)
      : 0,
  })),
})

export const getPromotionsSettings = async (
  storeId: string,
): Promise<PromotionsSettingsDocument> => {
  const raw = (await httpClient.json.get(
    promotionsSettingsUrl(storeId),
  )) as ApiPromotionsSettingsDocument | null
  return {
    promotions_settings: raw?.promotions_settings,
    membership_pricing: mapMembershipPricing(raw?.membership_pricing),
  }
}

export const updatePromotionsSettings = async (
  storeId: string,
  body: PromotionsSettingsDocument,
): Promise<PromotionsSettingsDocument> => {
  const raw = (await httpClient.json.post(
    promotionsSettingsUrl(storeId),
    body,
  )) as ApiPromotionsSettingsDocument | null
  return {
    promotions_settings: raw?.promotions_settings ?? body.promotions_settings,
    membership_pricing:
      mapMembershipPricing(raw?.membership_pricing) ?? body.membership_pricing,
  }
}

// ── Apple Wallet Pass config (Wallet tab) ────────────────────────────────────
// GET returns the full config document or `null` if no config has been created
// yet for the store. POST upserts the 9 required fields and returns the saved
// document. Snake_case wire shape is preserved end-to-end — no mapper needed.

export const getAppleWalletPassConfig = async (
  storeId: string,
): Promise<AppleWalletPassConfig | null> => {
  const raw = (await httpClient.json.get(
    appleWalletPassConfigUrl(storeId),
  )) as AppleWalletPassConfig | null
  return raw ?? null
}

export const upsertAppleWalletPassConfig = async (
  storeId: string,
  body: AppleWalletPassConfigInput,
): Promise<AppleWalletPassConfig> => {
  return (await httpClient.json.post(
    appleWalletPassConfigUrl(storeId),
    body,
  )) as AppleWalletPassConfig
}
